import React, {useEffect, useState} from 'react'
import {Button, Collapse, Descriptions, Form, Input, Modal, Row, Col, Table, DatePicker, Spin, Typography} from 'antd'
import {EyeOutlined, SearchOutlined} from '@ant-design/icons'
import {withTranslation} from 'react-i18next'
import moment from 'moment'

import intelligentQueryAPI from '../../../apis/intelligentQueryAPI'
import {PAGE_SIZE_DEFAULT} from '../../../constants/appConstant'
import {getPaginationConfig} from '../../../components/pagination'

const {RangePicker} = DatePicker
const {Panel} = Collapse

const QueryAudit = ({t}) => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState([])
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(PAGE_SIZE_DEFAULT)
  const [total, setTotal] = useState(0)
  const [detail, setDetail] = useState(null)

  const load = async (nextPage = page, nextSize = size) => {
    const values = form.getFieldsValue()
    const timeRange = values.timeRange || []
    setLoading(true)
    try {
      const response = await intelligentQueryAPI.queryAuditSearchAction({
        page: nextPage,
        size: nextSize,
        user: values.user,
        startTime: timeRange[0] && timeRange[0].format('YYYY-MM-DDTHH:mm:ss'),
        endTime: timeRange[1] && timeRange[1].format('YYYY-MM-DDTHH:mm:ss'),
      })
      const data = response.data.data || {}
      setItems(data.items || [])
      setTotal(data.total || 0)
      setPage(nextPage)
      setSize(nextSize)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load(1, PAGE_SIZE_DEFAULT)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const showDetail = async (chatId) => {
    setLoading(true)
    try {
      const response = await intelligentQueryAPI.queryAuditDetailAction(chatId)
      setDetail(response.data.data)
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {title: t('intelligentQuery.conversationTitle'), dataIndex: 'brief', ellipsis: true},
    {title: t('intelligentQuery.auditUser'), render: (_, record) => `${record.user?.name || '-'} (${record.user?.account || '-'})`},
    {title: t('intelligentQuery.questionCount'), dataIndex: 'record_count', width: 100, align: 'center'},
    {title: t('general.createTime'), dataIndex: 'create_time', render: value => value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : '-'},
    {title: t('intelligentQuery.lastQuestionTime'), dataIndex: 'last_question_time', render: value => value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : '-'},
    {title: t('general.action'), width: 90, align: 'center', render: (_, record) => <Button type="link" icon={<EyeOutlined/>} onClick={() => showDetail(record.id)}/>},
  ]

  return (
    <Spin spinning={loading}>
      <div className="container">
        <Form form={form} size="middle" className="searchForm" layout="vertical">
          <div className="searchFormTitle"><SearchOutlined/> {t('menu.queryAudit')}</div>
          <Row gutter={[10, 10]}>
            <Col span={6}>
              <Form.Item name="user" label={t('intelligentQuery.auditUser')}>
                <Input maxLength={255} allowClear placeholder={t('general.pleaseEnter')}/>
              </Form.Item>
            </Col>
            <Col span={10}>
              <Form.Item name="timeRange" label={t('intelligentQuery.auditTimeRange')}>
                <RangePicker showTime style={{width: '100%'}}/>
              </Form.Item>
            </Col>
            <Col span={8} style={{textAlign: 'right', marginTop: 30}}>
              <Button className="ant-btn-red" onClick={() => load(1, size)}>{t('general.search')}</Button>
            </Col>
          </Row>
        </Form>

        <Table
          className="searchResult"
          rowKey="id"
          dataSource={items}
          columns={columns}
          pagination={getPaginationConfig(page, size, total, load)}
        />

        <Modal
          visible={!!detail}
          title={t('intelligentQuery.conversationDetail')}
          width="90%"
          footer={null}
          destroyOnClose
          onCancel={() => setDetail(null)}
        >
          {detail && <>
            <Descriptions bordered size="small" column={3}>
              <Descriptions.Item label="ID">{detail.id}</Descriptions.Item>
              <Descriptions.Item label={t('intelligentQuery.auditUser')}>{detail.user?.name} ({detail.user?.account})</Descriptions.Item>
              <Descriptions.Item label={t('general.createTime')}>{detail.create_time && moment(detail.create_time).format('YYYY-MM-DD HH:mm:ss')}</Descriptions.Item>
              <Descriptions.Item label={t('intelligentQuery.conversationTitle')} span={3}>{detail.brief}</Descriptions.Item>
            </Descriptions>
            <Collapse style={{marginTop: 16}}>
              {(detail.records || []).map((record, index) => (
                <Panel header={`${index + 1}. ${record.question || '-'}`} key={record.id || index}>
                  <Descriptions bordered size="small" column={1}>
                    <Descriptions.Item label={t('intelligentQuery.questionDesc')}><Typography.Paragraph copyable>{record.question || '-'}</Typography.Paragraph></Descriptions.Item>
                    <Descriptions.Item label={t('intelligentQuery.answer')}><Typography.Paragraph copyable>{record.summary || record.analysis || record.chart_answer || record.sql_answer || record.datasource_select_answer || '-'}</Typography.Paragraph></Descriptions.Item>
                    <Descriptions.Item label="SQL"><Typography.Paragraph copyable>{record.sql || '-'}</Typography.Paragraph></Descriptions.Item>
                    <Descriptions.Item label={t('general.createTime')}>{record.create_time && moment(record.create_time).format('YYYY-MM-DD HH:mm:ss')}</Descriptions.Item>
                    <Descriptions.Item label={t('intelligentQuery.fullRecord')}>
                      <pre style={{maxHeight: 360, overflow: 'auto', whiteSpace: 'pre-wrap'}}>{JSON.stringify(record, null, 2)}</pre>
                    </Descriptions.Item>
                  </Descriptions>
                </Panel>
              ))}
            </Collapse>
          </>}
        </Modal>
      </div>
    </Spin>
  )
}

export default withTranslation()(QueryAudit)
